import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { api } from "../../lib/api";

const SuperAdminContext = createContext(null);

export function SuperAdminProvider({ children }) {
  const [loading, setLoading] = useState(true);
  const [setupComplete, setSetupComplete] = useState(false);
  const [authenticated, setAuthenticated] = useState(false);
  const [admin, setAdmin] = useState(null);

  const checkStatus = useCallback(async () => {
    try {
      const res = await api.get("/super-admin/status");
      setSetupComplete(Boolean(res.data?.setup_complete));
      setAuthenticated(Boolean(res.data?.authenticated));
      setAdmin(res.data?.admin || null);
    } catch (err) {
      console.error("Failed to fetch super admin status:", err);
      setSetupComplete(true);
      setAuthenticated(false);
      setAdmin(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkStatus();
  }, [checkStatus]);

  const login = async (username, password) => {
    const res = await api.post("/super-admin/login", { username, password });
    if (res.data?.ok) {
      setAuthenticated(true);
      setAdmin(res.data?.admin);
      return res.data;
    }
    throw new Error(res.data?.detail || "Login failed");
  };

  const setup = async (username, email, password) => {
    const res = await api.post("/super-admin/setup", { username, email, password });
    if (res.data?.ok) {
      setSetupComplete(true);
      setAuthenticated(true);
      setAdmin(res.data?.admin);
      return res.data;
    }
    throw new Error(res.data?.detail || "Setup failed");
  };

  const logout = async () => {
    try {
      await api.post("/super-admin/logout");
    } finally {
      setAuthenticated(false);
      setAdmin(null);
    }
  };

  return (
    <SuperAdminContext.Provider
      value={{
        loading,
        setupComplete,
        authenticated,
        admin,
        login,
        setup,
        logout,
        checkStatus,
      }}
    >
      {children}
    </SuperAdminContext.Provider>
  );
}

export function useSuperAdmin() {
  const ctx = useContext(SuperAdminContext);
  if (!ctx) {
    throw new Error("useSuperAdmin must be used within a SuperAdminProvider");
  }
  return ctx;
}
